/**
 * SettingsPage
 *
 * User settings and preferences management page
 */

import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import {
  useUserSettings,
  useUpdateUserPreferences,
} from '@/features/settings/hooks/useSettings';
import {
  ApiKeyManager,
  ChangePasswordForm,
  ThemeSelector,
  UserPreferencesForm,
} from '@/features/settings/components';
import type { UserPreferences } from '@/features/settings/types/settings.types';
import { DEFAULT_DISPLAY_TIMEZONE } from '@/constants/timezone';

const defaultPreferences: UserPreferences = {
  theme: 'system',
  language: 'en',
  timezone: DEFAULT_DISPLAY_TIMEZONE,
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  notifications: {
    email: true,
    browser: false,
    uploadComplete: true,
    schemaValidation: true,
    monitorAlerts: true,
  },
};

export const SettingsPage: React.FC = () => {
  const { toast } = useToast();
  const { data: settings, isLoading, error } = useUserSettings();
  const updatePreferences = useUpdateUserPreferences();

  // Lazy-initialized from `settings` in case it's already available on the
  // very first render (e.g. a warm query cache — user revisits /settings
  // within the stale/gc window), so the render-phase sync below — which
  // only reacts to `settings` *changing* — doesn't miss the case where it
  // never changes because it was there from the start.
  const [localPreferences, setLocalPreferences] = useState<UserPreferences>(
    () => settings?.preferences ?? defaultPreferences
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Re-sync local preferences if `settings` arrives (or changes) after
  // mount. Adjusted during render (not an effect) via a previous-value
  // mirror of `settings` itself: the query hook returns a referentially
  // stable object (TanStack Query's structural sharing keeps the same
  // reference across renders when the data is unchanged), so this is safe
  // to mirror directly. Converges on the very next render, so this fires
  // exactly once per `settings` identity change (matching the original
  // effect's [settings] dep).
  const [prevSettings, setPrevSettings] = useState(settings);
  if (settings !== prevSettings) {
    setPrevSettings(settings);
    if (settings?.preferences) {
      setLocalPreferences(settings.preferences);
    }
  }

  const handlePreferencesChange = (updates: Partial<UserPreferences>) => {
    setLocalPreferences((prev) => ({
      ...prev,
      ...updates,
      notifications: {
        ...prev.notifications,
        ...(updates.notifications || {}),
      },
    }));
    setHasChanges(true);
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    setLocalPreferences((prev) => ({ ...prev, theme }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updatePreferences.mutateAsync({
        preferences: localPreferences,
      });

      setHasChanges(false);

      toast({
        title: 'Settings saved',
        description: 'Your preferences have been updated successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to save settings',
        description: error.message || 'An error occurred while saving your settings',
        variant: 'destructive',
      });
    }
  };

  const handleReset = () => {
    if (settings?.preferences) {
      setLocalPreferences(settings.preferences);
      setHasChanges(false);
    }
  };

  // The Security (change password) card is self-contained — it does not depend
  // on the user-settings query — so it must render even while the rest of the
  // page is loading or has failed to load (e.g. the settings endpoint errors).
  // Otherwise a settings-load failure would make password change unreachable.
  const securityCard = (
    <Card>
      <CardHeader>
        <CardTitle>Security</CardTitle>
        <CardDescription>
          Change your password. Updating it signs out your other devices.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChangePasswordForm />
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-5xl py-8 space-y-8">
        <div className="space-y-2">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
        {securityCard}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-5xl py-8 space-y-8">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings and preferences
          </p>
        </div>
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load your preferences. Other settings are unavailable right
            now, but you can still change your password below.
          </AlertDescription>
        </Alert>
        {securityCard}
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl py-8 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>

          {hasChanges && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset}>
                Reset
              </Button>
              <Button onClick={handleSave} disabled={updatePreferences.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {updatePreferences.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Theme Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Customize the look and feel of the application</CardDescription>
        </CardHeader>
        <CardContent>
          <ThemeSelector
            value={localPreferences.theme}
            onChange={handleThemeChange}
          />
        </CardContent>
      </Card>

      {/* User Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>
            Configure your regional settings and notification preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserPreferencesForm
            preferences={localPreferences}
            onChange={handlePreferencesChange}
          />
        </CardContent>
      </Card>

      {/* Security / Change Password */}
      {securityCard}

      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Manage API keys for programmatic access to the lakehouse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ApiKeyManager />
        </CardContent>
      </Card>

      {/* Sticky Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6">
          <div className="flex gap-2 bg-background border rounded-lg shadow-lg p-3">
            <Button variant="outline" onClick={handleReset} size="sm">
              Reset
            </Button>
            <Button onClick={handleSave} disabled={updatePreferences.isPending} size="sm">
              <Save className="h-4 w-4 mr-2" />
              {updatePreferences.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
